import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dumbbell, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-background-paper border border-white/10 rounded-sm p-8 text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-4">Check Your Email</h1>
            <p className="text-neutral-400 mb-6">
              If an account exists with <span className="text-white">{email}</span>, you will receive a password reset link shortly.
            </p>
            <Link to="/login">
              <Button className="w-full bg-primary hover:bg-primary-hover font-bold">
                Back to Login
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-primary mb-6">
            <Dumbbell className="w-8 h-8" />
            <span className="font-heading text-2xl font-bold">T-FIT INC</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold mb-2">
            FORGOT <span className="text-primary">PASSWORD</span>
          </h1>
          <p className="text-neutral-400">
            Enter your email and we&apos;ll send you a reset link
          </p>
        </div>

        <div className="bg-background-paper border border-white/10 rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-background border-white/10"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary-hover font-bold"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'SENDING...' : 'SEND RESET LINK'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
